import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Shield,
  Eye,
  Database,
  Lock,
  Users,
  Globe,
  FileText,
  Settings,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import './Privacy.css';

const Privacy: React.FC = () => {
  return (
    <div className="privacy-container">
      {/* Header */}
      <div className="privacy-header">
        <div className="privacy-header-content">
          <div className="privacy-header-nav">
            <Link to="/signup">
              <Button variant="outline" size="sm" className="privacy-back-btn">
                <ArrowLeft className="w-4 h-4" />
                Back to Sign Up
              </Button>
            </Link>
            <div className="privacy-logo-section">
              <img
                src="/logo-mark.png"
                alt="EduExpertAI"
                className="privacy-logo-img"
              />
              <h1 className="privacy-logo-text">EduExpertAI</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="privacy-main">
        <Card className="privacy-card">
          <CardHeader className="privacy-card-header">
            <CardTitle className="privacy-card-title">Privacy Policy</CardTitle>
            <p className="privacy-last-updated">Last updated: December 2024</p>
          </CardHeader>

          <CardContent className="privacy-content">
            <div className="space-y-8">
              {/* Introduction */}
              <section className="privacy-section">
                <h2 className="privacy-section-title">
                  <Shield className="privacy-section-icon" />
                  1. Introduction and Our Commitment to Privacy
                </h2>
                <div className="privacy-section-content">
                  <p>
                    At EduExpertAI, protecting the privacy and security of our users—especially
                    students, educators, and educational institutions—is fundamental to our mission.
                    We understand the sensitive nature of educational data and are committed to
                    maintaining the highest standards of data protection and privacy.
                  </p>
                  <p>
                    This Privacy Policy explains how EduExpertAI Inc. ("we," "us," or "our")
                    collects, uses, protects, stores, and shares information when you use our
                    AI-powered educational technology platform and related services (collectively,
                    the "Platform").
                  </p>
                  <p>
                    <strong>Our Privacy Principles:</strong>
                  </p>
                  <ul>
                    <li>
                      <strong>Transparency:</strong> We clearly explain our data practices
                    </li>
                    <li>
                      <strong>Purpose Limitation:</strong> We only use data for legitimate
                      educational purposes
                    </li>
                    <li>
                      <strong>Data Minimization:</strong> We collect only the data necessary for our
                      services
                    </li>
                    <li>
                      <strong>Security First:</strong> We implement robust security measures to
                      protect your data
                    </li>
                    <li>
                      <strong>User Control:</strong> We provide you with choices about your data
                    </li>
                    <li>
                      <strong>Compliance:</strong> We adhere to applicable privacy laws and
                      educational regulations
                    </li>
                  </ul>
                </div>
              </section>

              {/* Legal Compliance */}
              <section className="privacy-section">
                <h2 className="privacy-section-title">
                  <FileText className="privacy-section-icon" />
                  2. Legal Compliance and Educational Privacy Laws
                </h2>
                <div className="privacy-section-content">
                  <p>
                    We comply with all applicable privacy laws and educational regulations,
                    including:
                  </p>

                  <h3>2.1 United States Laws</h3>
                  <ul>
                    <li>
                      <strong>FERPA (Family Educational Rights and Privacy Act):</strong> Protects
                      the privacy of student education records
                    </li>
                    <li>
                      <strong>COPPA (Children's Online Privacy Protection Act):</strong> Protects
                      the privacy of children under 13
                    </li>
                    <li>
                      <strong>PPRA (Protection of Pupil Rights Amendment):</strong> Governs student
                      surveys and data collection
                    </li>
                    <li>
                      <strong>State Privacy Laws:</strong> Including California CCPA, Illinois
                      SOPIPA, and others
                    </li>
                  </ul>

                  <h3>2.2 International Laws</h3>
                  <ul>
                    <li>
                      <strong>GDPR (General Data Protection Regulation):</strong> European Union
                      data protection law
                    </li>
                    <li>
                      <strong>PIPEDA:</strong> Canada's Personal Information Protection and
                      Electronic Documents Act
                    </li>
                    <li>
                      <strong>Other Regional Laws:</strong> We comply with applicable privacy laws
                      in all jurisdictions where we operate
                    </li>
                  </ul>

                  <h3>2.3 Educational Standards</h3>
                  <ul>
                    <li>
                      <strong>Student Data Privacy Consortium (SDPC) Standards</strong>
                    </li>
                    <li>
                      <strong>Future of Privacy Forum (FPF) Student Privacy Pledges</strong>
                    </li>
                    <li>
                      <strong>ISO 27001 Information Security Management Standards</strong>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Information We Collect */}
              <section className="privacy-section">
                <h2 className="privacy-section-title">
                  <Database className="privacy-section-icon" />
                  3. Information We Collect
                </h2>
                <div className="privacy-section-content">
                  <h3>3.1 Personal Information</h3>
                  <p>We collect personal information that you provide directly to us:</p>
                  <ul>
                    <li>
                      <strong>Account Information:</strong> Name, email address, username, password
                      (encrypted)
                    </li>
                    <li>
                      <strong>Profile Information:</strong> Profile picture, bio, educational
                      background, professional role
                    </li>
                    <li>
                      <strong>Contact Information:</strong> Phone number, institutional affiliation,
                      mailing address (when required)
                    </li>
                    <li>
                      <strong>Demographics:</strong> Age group, grade level, subject areas (for
                      appropriate content delivery)
                    </li>
                  </ul>

                  <h3>3.2 Educational Data</h3>
                  <p>We collect data related to your educational activities:</p>
                  <ul>
                    <li>
                      <strong>Learning Activities:</strong> Course participation, lesson completion,
                      time spent on activities
                    </li>
                    <li>
                      <strong>Assessment Data:</strong> Quiz responses, test scores, assignment
                      submissions, feedback
                    </li>
                    <li>
                      <strong>Content Creation:</strong> Courses, lessons, questions, and other
                      educational materials you create
                    </li>
                    <li>
                      <strong>Communication Data:</strong> Messages, discussion posts, collaborative
                      work
                    </li>
                    <li>
                      <strong>Progress Tracking:</strong> Learning paths, achievements, badges,
                      competency assessments
                    </li>
                    <li>
                      <strong>Analytics Data:</strong> Learning patterns, engagement metrics,
                      performance insights
                    </li>
                  </ul>

                  <h3>3.3 Technical Information</h3>
                  <p>We automatically collect certain technical data:</p>
                  <ul>
                    <li>
                      <strong>Device Information:</strong> Device type, operating system, browser
                      type and version
                    </li>
                    <li>
                      <strong>Usage Data:</strong> Pages visited, features used, click patterns,
                      session duration
                    </li>
                    <li>
                      <strong>Log Data:</strong> IP address, access times, error logs, security
                      events
                    </li>
                    <li>
                      <strong>Performance Data:</strong> Load times, system performance metrics,
                      error reports
                    </li>
                  </ul>

                  <h3>3.4 Cookies and Tracking Technologies</h3>
                  <p>We use cookies and similar technologies for:</p>
                  <ul>
                    <li>
                      <strong>Essential Cookies:</strong> Required for platform functionality and
                      security
                    </li>
                    <li>
                      <strong>Analytical Cookies:</strong> To understand platform usage and improve
                      services
                    </li>
                    <li>
                      <strong>Preference Cookies:</strong> To remember your settings and preferences
                    </li>
                    <li>
                      <strong>Security Cookies:</strong> To detect and prevent security threats
                    </li>
                  </ul>
                </div>
              </section>

              {/* How We Use Information */}
              <section className="privacy-section">
                <h2 className="privacy-section-title">
                  <Eye className="privacy-section-icon" />
                  4. How We Use Your Information
                </h2>
                <div className="privacy-section-content">
                  <h3>4.1 Educational Services</h3>
                  <p>We use your information to provide and improve our educational services:</p>
                  <ul>
                    <li>
                      <strong>Platform Functionality:</strong> Account management, user
                      authentication, platform access
                    </li>
                    <li>
                      <strong>Personalized Learning:</strong> Adaptive content delivery,
                      personalized recommendations, learning path optimization
                    </li>
                    <li>
                      <strong>Assessment and Feedback:</strong> Automated grading, progress
                      tracking, performance analytics
                    </li>
                    <li>
                      <strong>Collaboration Tools:</strong> Enabling communication between users,
                      group projects, peer learning
                    </li>
                    <li>
                      <strong>Content Delivery:</strong> Providing appropriate educational content
                      based on user level and needs
                    </li>
                  </ul>

                  <h3>4.2 Platform Improvement</h3>
                  <ul>
                    <li>
                      <strong>Service Enhancement:</strong> Analyzing usage patterns to improve
                      platform features
                    </li>
                    <li>
                      <strong>AI Development:</strong> Training our AI models to better serve
                      educational needs (using anonymized data)
                    </li>
                    <li>
                      <strong>Quality Assurance:</strong> Monitoring platform performance and user
                      experience
                    </li>
                    <li>
                      <strong>Research and Development:</strong> Developing new educational tools
                      and methodologies
                    </li>
                  </ul>

                  <h3>4.3 Communication and Support</h3>
                  <ul>
                    <li>
                      <strong>Customer Support:</strong> Responding to inquiries, technical support,
                      troubleshooting
                    </li>
                    <li>
                      <strong>Educational Communications:</strong> Platform updates, new features,
                      educational resources
                    </li>
                    <li>
                      <strong>Administrative Notices:</strong> Policy changes, security alerts,
                      maintenance notifications
                    </li>
                    <li>
                      <strong>Marketing Communications:</strong> Product updates and educational
                      insights (with consent)
                    </li>
                  </ul>

                  <h3>4.4 Legal and Security Purposes</h3>
                  <ul>
                    <li>
                      <strong>Compliance:</strong> Meeting legal obligations and educational
                      regulations
                    </li>
                    <li>
                      <strong>Security:</strong> Protecting against fraud, abuse, and security
                      threats
                    </li>
                    <li>
                      <strong>Rights Protection:</strong> Enforcing our terms of service and
                      protecting intellectual property
                    </li>
                    <li>
                      <strong>Legal Proceedings:</strong> Responding to legal requests and court
                      orders
                    </li>
                  </ul>
                </div>
              </section>

              {/* Information Sharing */}
              <section className="privacy-section">
                <h2 className="privacy-section-title">
                  <Users className="privacy-section-icon" />
                  5. How We Share Your Information
                </h2>
                <div className="privacy-section-content">
                  <h3>5.1 We Do NOT Sell Personal Data</h3>
                  <p>
                    <strong>
                      We do not sell, rent, or trade personal information, including student data,
                      to third parties for any purpose.
                    </strong>{' '}
                    This is a fundamental principle of our business.
                  </p>

                  <h3>5.2 Authorized Sharing</h3>
                  <p>We may share information in the following limited circumstances:</p>

                  <h4>Educational Institution Sharing</h4>
                  <ul>
                    <li>
                      <strong>Institutional Administrators:</strong> Progress reports, analytics,
                      and compliance data as required
                    </li>
                    <li>
                      <strong>Educators:</strong> Student progress and assessment data for their
                      classes
                    </li>
                    <li>
                      <strong>Parents/Guardians:</strong> Student progress information (with
                      appropriate consent and legal compliance)
                    </li>
                  </ul>

                  <h4>Service Providers</h4>
                  <ul>
                    <li>
                      <strong>Cloud Infrastructure:</strong> Secure hosting and data storage
                      providers
                    </li>
                    <li>
                      <strong>Analytics Services:</strong> Privacy-compliant analytics and
                      performance monitoring
                    </li>
                    <li>
                      <strong>Communication Tools:</strong> Email delivery, notification services,
                      customer support platforms
                    </li>
                    <li>
                      <strong>Security Services:</strong> Fraud detection, security monitoring, and
                      threat prevention
                    </li>
                  </ul>

                  <h4>Legal Requirements</h4>
                  <ul>
                    <li>
                      <strong>Legal Compliance:</strong> When required by law, regulation, or court
                      order
                    </li>
                    <li>
                      <strong>Safety Protection:</strong> To protect the safety of users or the
                      general public
                    </li>
                    <li>
                      <strong>Rights Enforcement:</strong> To protect our rights, property, or
                      safety
                    </li>
                  </ul>

                  <h3>5.3 Data Processing Agreements</h3>
                  <p>
                    All third-party service providers who may access user data are bound by strict
                    data processing agreements that require them to:
                  </p>
                  <ul>
                    <li>Use data only for specified educational purposes</li>
                    <li>Implement appropriate security measures</li>
                    <li>Comply with applicable privacy laws</li>
                    <li>Delete data when no longer needed</li>
                    <li>Provide regular security and compliance reports</li>
                  </ul>
                </div>
              </section>

              {/* Data Security */}
              <section className="privacy-section">
                <h2 className="privacy-section-title">
                  <Lock className="privacy-section-icon" />
                  6. Data Security and Protection
                </h2>
                <div className="privacy-section-content">
                  <h3>6.1 Security Infrastructure</h3>
                  <p>We implement industry-leading security measures:</p>
                  <ul>
                    <li>
                      <strong>Encryption:</strong> 256-bit SSL/TLS encryption for data in transit,
                      AES-256 encryption for data at rest
                    </li>
                    <li>
                      <strong>Secure Data Centers:</strong> SOC 2 Type II compliant facilities with
                      24/7 monitoring
                    </li>
                    <li>
                      <strong>Access Controls:</strong> Multi-factor authentication, role-based
                      access, principle of least privilege
                    </li>
                    <li>
                      <strong>Network Security:</strong> Firewalls, intrusion detection, DDoS
                      protection, VPN access
                    </li>
                    <li>
                      <strong>Regular Audits:</strong> Annual security assessments, penetration
                      testing, vulnerability scans
                    </li>
                  </ul>

                  <h3>6.2 Operational Security</h3>
                  <ul>
                    <li>
                      <strong>Employee Training:</strong> Regular security and privacy training for
                      all staff
                    </li>
                    <li>
                      <strong>Background Checks:</strong> Comprehensive screening for employees with
                      data access
                    </li>
                    <li>
                      <strong>Incident Response:</strong> 24/7 security monitoring and rapid
                      incident response procedures
                    </li>
                    <li>
                      <strong>Data Backup:</strong> Regular, encrypted backups with secure recovery
                      procedures
                    </li>
                    <li>
                      <strong>Vendor Management:</strong> Rigorous security assessments of all
                      third-party providers
                    </li>
                  </ul>

                  <h3>6.3 Compliance Certifications</h3>
                  <ul>
                    <li>
                      <strong>SOC 2 Type II:</strong> Annual compliance with security, availability,
                      and confidentiality criteria
                    </li>
                    <li>
                      <strong>ISO 27001:</strong> Information security management system
                      certification
                    </li>
                    <li>
                      <strong>FERPA Compliance:</strong> Rigorous adherence to educational privacy
                      requirements
                    </li>
                    <li>
                      <strong>GDPR Compliance:</strong> Full compliance with European data
                      protection standards
                    </li>
                  </ul>
                </div>
              </section>

              {/* Data Retention */}
              <section className="privacy-section">
                <h2 className="privacy-section-title">
                  <Settings className="privacy-section-icon" />
                  7. Data Retention and Deletion
                </h2>
                <div className="privacy-section-content">
                  <h3>7.1 Retention Periods</h3>
                  <ul>
                    <li>
                      <strong>Active User Data:</strong> Retained while account is active and for
                      educational purposes
                    </li>
                    <li>
                      <strong>Student Records:</strong> Retained according to FERPA requirements and
                      institutional policies
                    </li>
                    <li>
                      <strong>Assessment Data:</strong> Retained for academic record-keeping and
                      progress tracking
                    </li>
                    <li>
                      <strong>Technical Logs:</strong> Retained for 12-24 months for security and
                      performance monitoring
                    </li>
                    <li>
                      <strong>Backup Data:</strong> Securely deleted within 90 days of primary data
                      deletion
                    </li>
                  </ul>

                  <h3>7.2 Data Deletion</h3>
                  <p>We delete personal data when:</p>
                  <ul>
                    <li>The retention period expires</li>
                    <li>You request deletion (subject to legal requirements)</li>
                    <li>Your account is terminated</li>
                    <li>The data is no longer necessary for educational purposes</li>
                    <li>Required by law or regulation</li>
                  </ul>

                  <h3>7.3 User-Requested Deletion</h3>
                  <p>
                    You can request deletion of your personal data, subject to legal and legitimate
                    educational requirements. We will respond to deletion requests within 30 days
                    and provide confirmation of data deletion.
                  </p>
                </div>
              </section>

              {/* Your Rights and Choices */}
              <section className="privacy-section">
                <h2 className="privacy-section-title">
                  <Globe className="privacy-section-icon" />
                  8. Your Rights and Choices
                </h2>
                <div className="privacy-section-content">
                  <h3>8.1 Access and Control</h3>
                  <p>You have the right to:</p>
                  <ul>
                    <li>
                      <strong>Access:</strong> Request a copy of your personal data
                    </li>
                    <li>
                      <strong>Correction:</strong> Update or correct inaccurate information
                    </li>
                    <li>
                      <strong>Deletion:</strong> Request deletion of your personal data (subject to
                      limitations)
                    </li>
                    <li>
                      <strong>Portability:</strong> Export your data in a commonly used format
                    </li>
                    <li>
                      <strong>Restriction:</strong> Limit how we process your data
                    </li>
                    <li>
                      <strong>Objection:</strong> Object to certain types of data processing
                    </li>
                  </ul>

                  <h3>8.2 Student and Minor Rights</h3>
                  <p>For users under 18:</p>
                  <ul>
                    <li>Parents/guardians may access their child's educational records</li>
                    <li>
                      Parents/guardians may request correction or deletion of their child's data
                    </li>
                    <li>
                      Students gain full control of their records upon reaching 18 (or age of
                      majority)
                    </li>
                    <li>
                      We do not collect more data from minors than necessary for educational
                      purposes
                    </li>
                  </ul>

                  <h3>8.3 Communication Preferences</h3>
                  <p>You can control:</p>
                  <ul>
                    <li>Email notification settings</li>
                    <li>Marketing communication preferences</li>
                    <li>Platform notification settings</li>
                    <li>Data sharing preferences (where applicable)</li>
                  </ul>
                </div>
              </section>

              {/* International Transfers */}
              <section className="privacy-section">
                <h2 className="privacy-section-title">9. International Data Transfers</h2>
                <div className="privacy-section-content">
                  <p>
                    Our Platform operates globally, and data may be transferred to and processed in
                    countries other than your home country. We ensure all international transfers
                    comply with applicable privacy laws through:
                  </p>
                  <ul>
                    <li>
                      <strong>Adequacy Decisions:</strong> Transfers to countries with adequate
                      protection
                    </li>
                    <li>
                      <strong>Standard Contractual Clauses:</strong> EU-approved data transfer
                      agreements
                    </li>
                    <li>
                      <strong>Binding Corporate Rules:</strong> Internal data protection standards
                    </li>
                    <li>
                      <strong>Certification Programs:</strong> Participation in recognized privacy
                      frameworks
                    </li>
                  </ul>
                </div>
              </section>

              {/* Changes to Policy */}
              <section className="privacy-section">
                <h2 className="privacy-section-title">10. Changes to This Privacy Policy</h2>
                <div className="privacy-section-content">
                  <p>
                    We may update this Privacy Policy periodically to reflect changes in our
                    practices, technology, legal requirements, or other factors. We will:
                  </p>
                  <ul>
                    <li>Notify users of material changes via email and platform notifications</li>
                    <li>Provide at least 30 days' notice before significant changes take effect</li>
                    <li>Maintain previous versions for reference</li>
                    <li>
                      Seek additional consent for material changes affecting how we use your data
                    </li>
                  </ul>
                  <p>
                    Continued use of our Platform after changes constitutes acceptance of the
                    updated Privacy Policy, unless the changes require additional consent.
                  </p>
                </div>
              </section>

              {/* Contact Information */}
              <section className="privacy-section">
                <h2 className="privacy-section-title">Contact Information</h2>
                <div className="privacy-contact">
                  <div className="privacy-contact-title">
                    For privacy-related questions, concerns, or requests, please contact us:
                  </div>
                  <p className="privacy-contact-text">
                    <strong>Privacy Officer:</strong>{' '}
                    <a href="mailto:privacy@eduexpertai.com" className="privacy-contact-email">
                      privacy@eduexpertai.com
                    </a>
                    <br />
                    <strong>Data Protection Officer (DPO):</strong>{' '}
                    <a href="mailto:dpo@eduexpertai.com" className="privacy-contact-email">
                      dpo@eduexpertai.com
                    </a>
                    <br />
                    <strong>Student Privacy Inquiries:</strong>{' '}
                    <a
                      href="mailto:studentprivacy@eduexpertai.com"
                      className="privacy-contact-email"
                    >
                      studentprivacy@eduexpertai.com
                    </a>
                    <br />
                    <strong>Legal Department:</strong>{' '}
                    <a href="mailto:legal@eduexpertai.com" className="privacy-contact-email">
                      legal@eduexpertai.com
                    </a>
                    <br />
                    <strong>Mailing Address:</strong>
                    <br />
                    EduExpertAI  , Vijaynagara bangalore
                    <br />
                    <strong>Phone:</strong> +917483868010
                    <br />
                    <strong>Privacy Hotline:</strong> Available 24/7 for urgent privacy matters
                  </p>
                  <p className="privacy-contact-text">
                    <strong>Response Time:</strong> We respond to privacy inquiries within 72 hours
                    and resolve most requests within 30 days.
                  </p>
                </div>
              </section>
            </div>
          </CardContent>
        </Card>

        {/* Back to signup */}
        <div className="text-center mt-8">
          <Link to="/signup">
            <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700">
              Back to Sign Up
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
